import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useTheme } from "next-themes";
import {
  Anchor, LayoutDashboard, Users2, PhoneCall, BarChart3, Settings as Cog,
  Moon, Sun, LogOut, Menu, ShieldUser, ChevronDown, ScrollText, GitCompare, Flame, Trophy, UserCog, History, Landmark, Radar, BookUser, Grid3x3, Zap, Building2, FileSignature, BookOpen, LifeBuoy, Bell, MessageCircle, CalendarDays, FileSpreadsheet, Trash2, Vote, Plane,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import ScreenshotGuard from "./ScreenshotGuard";
import PrivacyNotice from "./PrivacyNotice";
import MessagesDrawer from "./MessagesDrawer";
import AnnouncementBar from "./AnnouncementBar";
import { isGeneralManager, canRadar } from "@/lib/roles";
import OnboardingDialog from "./OnboardingDialog";
import { useStatusFilter } from "@/context/StatusFilterContext";
import { toast } from "sonner";

const beep = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine"; o.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    o.start();
    o.stop(ctx.currentTime + 0.36);
    setTimeout(() => ctx.close().catch(() => {}), 600);
  } catch { /* ignore */ }
};

const gmOrBranch = (u) => u?.role === "super_admin" || u?.role === "branch_manager";
const fmtShort = (iso) => { try { return new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" }); } catch { return ""; } };

const NAV_GROUPS = [
  {
    key: "panel", label: "Panel", items: [
      { to: "/panel", label: "Genel Panel", icon: LayoutDashboard, roles: ["super_admin", "committee_manager", "chairman", "branch_manager"] },
      { to: "/analizler", label: "Analizler", icon: BarChart3, roles: ["super_admin", "committee_manager", "field_agent", "chairman", "branch_manager"] },
    ],
  },
  {
    key: "saha", label: "Saha", items: [
      { to: "/secmenler", label: "Seçmen Listesi", icon: Users2, roles: ["super_admin", "committee_manager", "field_agent", "chairman", "branch_manager"] },
      { to: "/arama", label: "Arama Asistanı", icon: PhoneCall, roles: ["super_admin", "committee_manager", "field_agent", "chairman", "branch_manager"] },
      { to: "/rehber", label: "Rehberimdeki Firmalar", icon: BookUser, roles: ["super_admin", "committee_manager", "field_agent", "chairman", "branch_manager"] },
      { to: "/oncelik", label: "Akıllı Öncelik", icon: Flame, roles: ["super_admin", "committee_manager", "field_agent", "chairman", "branch_manager"] },
      { to: "/havuz", label: "Havuz", icon: LifeBuoy, roles: ["super_admin", "committee_manager", "field_agent", "chairman", "branch_manager"] },
      { to: "/radar", label: "Radar", icon: Radar, roles: ["super_admin", "committee_manager", "branch_manager"], visible: canRadar },
    ],
  },
  {
    key: "takip", label: "Takip", items: [
      { to: "/cetele", label: "Seçim Çetelesi", icon: Vote, roles: ["super_admin", "committee_manager", "field_agent", "chairman", "branch_manager"] },
      { to: "/karne", label: "Komite Karnesi", icon: Trophy, roles: ["super_admin", "committee_manager", "chairman"] },
      { to: "/sube-panel", label: "Şube Paneli", icon: Building2, roles: ["super_admin", "committee_manager", "branch_manager"], visible: gmOrBranch },
      { to: "/sube-karnesi", label: "Şube Karnesi", icon: Landmark, roles: ["super_admin", "committee_manager", "branch_manager"], visible: gmOrBranch },
      { to: "/katman-farklari", label: "Katman Farkları", icon: GitCompare, roles: ["super_admin", "committee_manager", "branch_manager"], visible: gmOrBranch },
      { to: "/yetki-takip", label: "Yetki Belgesi Takip", icon: FileSignature, roles: ["super_admin", "committee_manager", "branch_manager"] },
      { to: "/seyahat-plani", label: "Seyahat Planı", icon: Plane, roles: ["super_admin", "committee_manager", "chairman", "branch_manager"] },
      { to: "/takim", label: "Takım Performansı", icon: BarChart3, roles: ["super_admin", "committee_manager", "chairman"] },
    ],
  },
  {
    key: "yonetim", label: "Yönetim", items: [
      { to: "/kullanicilar", label: "Kullanıcılar", icon: ShieldUser, roles: ["super_admin", "committee_manager", "chairman"] },
      { to: "/kullanici-raporlari", label: "Kullanıcı Karnesi", icon: BarChart3, roles: ["super_admin"] },
      { to: "/sorumlular", label: "Sorumlu Yönetimi", icon: UserCog, roles: ["super_admin", "committee_manager"] },
      { to: "/gecmis-sonuclar", label: "Geçmiş Seçim Sonuçları", icon: Landmark, roles: ["super_admin"] },
      { to: "/full-liste-esitleme", label: "FULL Liste Eşitleme", icon: FileSpreadsheet, roles: ["super_admin"] },
      { to: "/silme-talepleri", label: "Silme Talepleri", icon: Trash2, roles: ["super_admin", "committee_manager"] },
      { to: "/gunluk", label: "İşlem Günlüğü", icon: ScrollText, roles: ["super_admin", "committee_manager", "chairman"] },
      { to: "/oturumlar", label: "Giriş Kayıtları", icon: History, roles: ["super_admin"] },
    ],
  },
  {
    key: "yardim", label: "Yardım", items: [
      { to: "/cetele-kilavuzu", label: "Çetele Kılavuzu (Resimli)", icon: BookOpen, roles: ["super_admin", "committee_manager", "field_agent", "chairman", "branch_manager"] },
      { to: "/kilavuz", label: "Kullanım Kılavuzu", icon: BookOpen, roles: ["super_admin", "committee_manager", "field_agent", "chairman", "branch_manager"] },
      { to: "/ayarlar", label: "Ayarlar & Güvenlik", icon: Cog, roles: ["super_admin", "committee_manager", "field_agent", "chairman", "branch_manager"] },
    ],
  },
];


export default function AppLayout() {
  const { user, logout, committee, setCommittee } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [committees, setCommittees] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [poolUnseen, setPoolUnseen] = useState(0);
  const { status, setStatus } = useStatusFilter();
  useEffect(() => {
    if (user && !isGeneralManager(user) && status !== "faal") setStatus("faal");
  }, [user, status, setStatus]);
  const [claimsUnseen, setClaimsUnseen] = useState(0);
  const [poolClaims, setPoolClaims] = useState([]);
  const [poolRecent, setPoolRecent] = useState([]);
  const [msgUnread, setMsgUnread] = useState(0);
  const [msgOpen, setMsgOpen] = useState(false);
  const [myTasks, setMyTasks] = useState([]);
  const [tasksUnseen, setTasksUnseen] = useState(0);
  const [merkezRows, setMerkezRows] = useState([]);
  const [merkezUnseen, setMerkezUnseen] = useState(0);
  const canMerkez = !!user && (user.merkez_approver || user.role === "super_admin");

  useEffect(() => {
    if (!canMerkez) return;
    let alive = true;
    let prev = -1;
    const tick = async () => {
      try {
        const { data } = await api.get("/yetki/merkez-pending");
        if (!alive) return;
        const rows = data.rows || [];
        const seen = localStorage.getItem(`imeak_merkez_seen_${user.id}`) || "";
        setMerkezRows(rows);
        setMerkezUnseen(seen ? rows.filter((r) => (r.merkez_pending_at || "") > seen).length : rows.length);
        if (prev >= 0 && rows.length > prev) {
          beep();
          toast.info(`${rows.length - prev} yeni belge Seçim Ofisi onayı bekliyor`, {
            description: "Ofisteki evrakı kontrol edip onaylayın.",
            duration: 10000,
            action: { label: "Onaya Git", onClick: () => navigate("/yetki-takip?tab=merkez") },
          });
        }
        prev = rows.length;
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 20000);
    const onChanged = () => tick();
    window.addEventListener("merkez-pending-changed", onChanged);
    return () => { alive = false; clearInterval(id); window.removeEventListener("merkez-pending-changed", onChanged); };
  }, [canMerkez, user?.id, navigate]);

  useEffect(() => {
    api.get("/committees").then(({ data }) => setCommittees(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    const key = `imeak_visited_${user.id}`;
    try {
      const arr = JSON.parse(localStorage.getItem(key) || "[]");
      if (!arr.includes(location.pathname)) {
        arr.push(location.pathname);
        localStorage.setItem(key, JSON.stringify(arr));
      }
    } catch { /* ignore */ }
  }, [location.pathname, user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    let prevPool = -1;
    const refresh = async () => {
      try {
        const since = localStorage.getItem(`imeak_pool_seen_${user.id}`) || undefined;
        const claimsSeen = localStorage.getItem(`imeak_claims_seen_${user.id}`) || "";
        const [a, c] = await Promise.all([
          api.get("/pool/unseen", { params: since ? { since } : {} }),
          api.get("/pool/claims"),
        ]);
        if (!alive) return;
        try {
          const { data: tks } = await api.get("/tasks", { params: { scope: "mine", status: "open" } });
          const taskSeen = localStorage.getItem(`imeak_tasks_seen_${user.id}`) || "";
          setMyTasks(tks);
          setTasksUnseen(taskSeen ? tks.filter((t) => t.created_at > taskSeen).length : tks.length);
        } catch { /* ignore */ }
        const poolCount = a.data.count || 0;
        if (prevPool >= 0 && poolCount > prevPool) {
          const yeni = poolCount - prevPool;
          beep();
          toast.info(`Havuza ${yeni} yeni firma eklendi`, {
            description: "Havuzu açıp sahipsiz firmaları devralabilirsiniz.",
            duration: 10000,
            action: { label: "Havuzu Aç", onClick: () => navigate("/havuz") },
          });
        }
        prevPool = poolCount;
        setPoolUnseen(poolCount);
        const claims = c.data || [];
        setPoolClaims(claims);
        setClaimsUnseen(claims.filter((x) => !claimsSeen || (x.claimed_at || "") > claimsSeen).length);
      } catch { /* ignore */ }
    };
    refresh();
    const id = setInterval(refresh, 20000);
    const onSeen = () => setPoolUnseen(0);
    window.addEventListener("pool-seen", onSeen);
    return () => { alive = false; clearInterval(id); window.removeEventListener("pool-seen", onSeen); };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    let prev = -1;
    const tick = async () => {
      try {
        const { data } = await api.get("/messages/unread");
        if (!alive) return;
        const n = data.total || 0;
        if (prev >= 0 && n > prev) beep();
        prev = n;
        setMsgUnread(n);
        document.title = n > 0 ? `(${n}) Seçim Bürosu` : "Seçim Bürosu";
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 10000);
    return () => { alive = false; clearInterval(id); document.title = "Seçim Bürosu"; };
  }, [user?.id]);

  // Aktivite sinyali: hangi sayfada / hangi komitede vakit geçirildiği (dakika bazlı)
  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    const ping = () => {
      if (!alive || document.visibilityState === "hidden") return;
      api.post("/activity/ping", {
        page: location.pathname,
        committee_no: committee && committee !== "all" ? Number(committee) : null,
      }).catch(() => { /* sessizce yut */ });
    };
    ping();
    const id = setInterval(ping, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [user?.id, location.pathname, committee]);

  const onBellOpen = async (o) => {
    if (!o) return;
    try {
      const { data } = await api.get("/pool", { params: { limit: 5 } });
      setPoolRecent(data);
    } catch { /* ignore */ }
    const now = new Date().toISOString();
    try {
      localStorage.setItem(`imeak_pool_seen_${user.id}`, now);
      localStorage.setItem(`imeak_claims_seen_${user.id}`, now);
    } catch { /* ignore */ }
    setPoolUnseen(0);
    setClaimsUnseen(0);
    setTasksUnseen(0);
    setMerkezUnseen(0);
    try {
      localStorage.setItem(`imeak_tasks_seen_${user.id}`, now);
      localStorage.setItem(`imeak_merkez_seen_${user.id}`, now);
    } catch { /* ignore */ }
  };
  const bellTotal = poolUnseen + claimsUnseen + tasksUnseen + merkezUnseen;

  const groups = NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((n) => n.roles.includes(user?.role) && (!n.visible || n.visible(user))) }))
    .filter((g) => g.items.length);
  const activeGroup = groups.find((g) => g.items.some((i) => location.pathname.startsWith(i.to)))?.key
    || groups[0]?.key;
  const [openGroup, setOpenGroup] = useState(activeGroup);
  useEffect(() => { if (activeGroup) setOpenGroup(activeGroup); }, [activeGroup]);

  const SidebarInner = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 px-4 h-16 border-b border-white/10">
        <div className="h-9 w-9 rounded-lg bg-cyan-500 grid place-items-center text-white shrink-0">
          <Anchor className="h-5 w-5" />
        </div>
        {!collapsed && (
          <div className="leading-tight">
            <p className="font-display font-bold text-white text-sm">Seçim Bürosu</p>
            <p className="text-[11px] text-cyan-200/70">Yönetim Paneli</p>
          </div>
        )}
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {groups.map((g) => {
          const open = collapsed || openGroup === g.key;
          const hasActive = g.items.some((i) => location.pathname.startsWith(i.to));
          return (
            <div key={g.key} className="pb-0.5">
              {!collapsed && (
                <button
                  type="button"
                  onClick={() => setOpenGroup(open ? null : g.key)}
                  data-testid={`nav-group-${g.key}`}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide transition-colors ${
                    hasActive ? "text-cyan-300" : "text-slate-400 hover:text-slate-200"
                  }`}
                >
                  <span>{g.label}</span>
                  {!open && hasActive && <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />}
                  <ChevronDown className={`h-3.5 w-3.5 ml-auto transition-transform ${open ? "" : "-rotate-90"}`} />
                </button>
              )}
              {open && g.items.map((it) => {
                const active = location.pathname === it.to || location.pathname.startsWith(`${it.to}/`);
                const Icon = it.icon;
                return (
                  <Link
                    key={it.to}
                    to={it.to}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`nav-${it.to.replace("/", "")}`}
                    className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                      active ? "bg-cyan-500 text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    <Icon className="h-[18px] w-[18px] shrink-0" />
                    {!collapsed && <span>{it.label}</span>}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>
      <div className="p-3 border-t border-white/10">
        {!collapsed && (
          <div className="px-2 py-1 text-[11px] text-slate-400">
            {ROLE_LABELS[user?.role]}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {!isGeneralManager(user) && <ScreenshotGuard />}
      {/* Desktop sidebar */}
      <aside className={`hidden md:flex flex-col bg-[#0F2C59] transition-all duration-200 ${collapsed ? "w-[68px]" : "w-64"}`}>
        {SidebarInner}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-[#0F2C59]">{SidebarInner}</aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {!isGeneralManager(user) && <PrivacyNotice />}

        <AnnouncementBar />
        <header className="sticky top-0 z-40 h-16 bg-card/90 backdrop-blur-xl border-b border-border flex items-center gap-3 px-4">
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
            <Menu className="h-5 w-5" />
          </Button>
          <Button variant="ghost" size="icon" className="hidden md:inline-flex" onClick={() => setCollapsed((c) => !c)} data-testid="collapse-sidebar-button">
            <Menu className="h-5 w-5" />
          </Button>

          {(user?.role === "super_admin" || (user?.role === "committee_manager" && (user?.all_committees || (user?.committee_nos || []).length > 1))) ? (
            <Select value={String(committee)} onValueChange={(v) => setCommittee(v === "all" ? "all" : Number(v))}>
              <SelectTrigger className="w-[260px] max-w-[55vw]" data-testid="committee-selector">
                <SelectValue placeholder="Komite seçin" />
              </SelectTrigger>
              <SelectContent className="max-h-[400px]">
                <SelectItem value="all">Genel Durum (Tüm Komiteler)</SelectItem>
                {committees.map((c) => (
                  <SelectItem key={c.committee_no} value={String(c.committee_no)}>
                    #{c.committee_no} - {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : user?.role === "branch_manager" ? (
            <div className="text-sm font-semibold text-foreground truncate" data-testid="branch-label">
              {((user?.branch_names || []).join(", ")) || "Şube"} Şubesi
            </div>
          ) : (
            <div className="text-sm font-medium text-foreground truncate">
              Komite #{user?.committee_no ?? (user?.committee_nos && user.committee_nos[0])}
              {committees[0] && <span className="text-muted-foreground"> - {committees[0].name}</span>}
            </div>
          )}

          <div className="ml-auto flex items-center gap-2">
            {isGeneralManager(user) ? (
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-9 w-[130px] hidden sm:flex" data-testid="global-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="faal"><span className="flex items-center gap-1.5">Faal <span className="text-[9px] px-1 py-0.5 rounded bg-cyan-100 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300 font-semibold">Varsayılan</span></span></SelectItem>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="aski">Askı</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <span className="h-9 px-3 hidden sm:inline-flex items-center rounded-md border border-border text-sm font-medium text-muted-foreground"
                data-testid="global-status-fixed">Faal</span>
            )}
            <Button variant="ghost" size="icon" className="relative" data-testid="messages-btn" title="Mesajlar" onClick={() => setMsgOpen(true)}>
              <MessageCircle className="h-5 w-5" />
              {msgUnread > 0 && (
                <span data-testid="messages-unread-count" className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold grid place-items-center pointer-events-none">
                  {msgUnread > 99 ? "99+" : msgUnread}
                </span>
              )}
            </Button>
            <DropdownMenu onOpenChange={onBellOpen}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="pool-bell" title="Havuz bildirimleri">
                  <Bell className="h-5 w-5" />
                  {bellTotal > 0 && (
                    <span data-testid="pool-bell-count" className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center pointer-events-none">
                      {bellTotal > 99 ? "99+" : bellTotal}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80" data-testid="pool-bell-menu">
                <DropdownMenuLabel>Bildirimler</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {myTasks.length > 0 && (
                  <>
                    <div className="px-2 py-1 text-[11px] font-semibold text-cyan-600">Arama görevlerim ({myTasks.length})</div>
                    {myTasks.slice(0, 4).map((t) => (
                      <DropdownMenuItem key={t.id} className="flex flex-col items-start gap-0.5" data-testid={`bell-task-${t.id}`} onClick={() => navigate("/panel")}>
                        <span className={`text-xs font-medium truncate w-full ${t.overdue ? "text-red-600" : ""}`}>
                          {t.overdue ? "⚠ Süresi geçti · " : ""}{t.voter_name}
                        </span>
                        <span className="text-[11px] text-muted-foreground truncate w-full">{t.assigner_name} · {fmtShort(t.created_at)}</span>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                  </>
                )}
                {canMerkez && merkezRows.length > 0 && (
                  <>
                    <div className="px-2 py-1 text-[11px] font-semibold text-amber-600">Seçim Ofisi onayı bekleyen belgeler ({merkezRows.length})</div>
                    {merkezRows.slice(0, 5).map((r) => (
                      <DropdownMenuItem key={r.id} className="flex flex-col items-start gap-0.5" data-testid={`bell-merkez-${r.id}`}
                        onClick={() => navigate("/yetki-takip?tab=merkez")}>
                        <span className="text-xs font-medium truncate w-full">{r.company_name}</span>
                        <span className="text-[11px] text-muted-foreground truncate w-full">#{r.committee_no} · {r.merkez_pending_by} · {fmtShort(r.merkez_pending_at)}</span>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                  </>
                )}
                {poolClaims.length > 0 && (
                  <>
                    <div className="px-2 py-1 text-[11px] font-semibold text-emerald-600">Firmalarınız devralındı</div>
                    {poolClaims.slice(0, 5).map((c) => (
                      <DropdownMenuItem key={c.id} className="flex flex-col items-start gap-0.5" data-testid={`bell-claim-${c.voter_id}`} onClick={() => navigate("/havuz")}>
                        <span className="text-xs font-medium truncate w-full">{c.company_name}</span>
                        <span className="text-[11px] text-muted-foreground"><b>{c.claimed_by_name}</b> devraldı · {fmtShort(c.claimed_at)}</span>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                  </>
                )}
                <div className="px-2 py-1 text-[11px] font-semibold text-muted-foreground">Son havuz firmaları</div>
                {poolRecent.length === 0 ? (
                  <div className="px-2 py-3 text-xs text-muted-foreground text-center" data-testid="bell-empty">Havuz boş</div>
                ) : (
                  poolRecent.map((v) => (
                    <DropdownMenuItem key={v.id} className="flex flex-col items-start gap-0.5" data-testid={`bell-pool-${v.id}`} onClick={() => navigate("/havuz")}>
                      <span className="text-xs font-medium truncate w-full">{v.company_name}</span>
                      <span className="text-[11px] text-muted-foreground truncate w-full">#{v.committee_no} · {v.branch} · {v.added_by_name}</span>
                    </DropdownMenuItem>
                  ))
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/havuz")} data-testid="bell-see-all" className="justify-center text-cyan-600 font-medium">Tümünü Gör</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="icon" title="Başlangıç Rehberi"
              onClick={() => window.dispatchEvent(new Event("imeak:onboarding"))} data-testid="onboarding-reopen">
              <LifeBuoy className="h-5 w-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} data-testid="theme-toggle">
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 px-2" data-testid="user-menu-button">
                  <div className="h-8 w-8 rounded-full bg-[#0F2C59] text-white grid place-items-center text-xs font-semibold">
                    {(user?.name || "U").slice(0, 2).toUpperCase()}
                  </div>
                  <span className="hidden sm:inline text-sm font-medium max-w-[120px] truncate">{user?.name}</span>
                  <ChevronDown className="h-4 w-4 hidden sm:inline" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-medium">{user?.name}</div>
                  <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/ayarlar")} data-testid="menu-settings">
                  <Cog className="h-4 w-4 mr-2" /> Ayarlar & Güvenlik
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }} className="text-destructive" data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" /> Çıkış Yap
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 pb-24 md:pb-6 max-w-[1600px] w-full mx-auto">
          <Outlet />
        </main>
      </div>

      <MessagesDrawer open={msgOpen} onOpenChange={setMsgOpen} onRead={() => setMsgUnread(0)} />

      {/* Mobile bottom navigation */}
      <nav className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-card/95 backdrop-blur-xl border-t border-border" data-testid="mobile-bottom-nav">
        <div className="flex items-stretch justify-around">
          {groups.flatMap((g) => g.items)
            .filter((n) => ["/panel", "/secmenler", "/arama", "/oncelik"].includes(n.to))
            .map((it) => {
              const active = location.pathname === it.to;
              const Icon = it.icon;
              return (
                <Link
                  key={it.to}
                  to={it.to}
                  data-testid={`bottomnav-${it.to.replace("/", "")}`}
                  className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-2 text-[10px] font-medium transition-colors ${
                    active ? "text-cyan-500" : "text-muted-foreground"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="truncate max-w-full px-0.5">{it.label.split(" ")[0]}</span>
                </Link>
              );
            })}
          <button
            onClick={() => setMobileOpen(true)}
            data-testid="bottomnav-menu"
            className="flex flex-col items-center justify-center gap-0.5 flex-1 py-2 text-[10px] font-medium text-muted-foreground transition-colors"
          >
            <Menu className="h-5 w-5" />
            <span>Menü</span>
          </button>
        </div>
      </nav>
      <OnboardingDialog userId={user?.id} />
    </div>
  );
}
